import React, { useRef } from 'react';
import 'twin.macro';
import { useHydrateAtoms } from 'jotai/react/utils';
import Dialog from '@teetsh/app/src/components/Dialog';
import {
  selectedPreviewSeancesAtom,
  selectedSchoolSchoolyearAtom,
  submitPayloadAtom,
  useCloseCopyEventsToAnotherDayModal,
  useCopyEventsToAnotherDayPayload,
  useDestSchoolSchoolyear,
  useFromValue,
  useResetSelectedPreviewSeances,
  useSetDestSchool,
  useSetDestSchoolyear,
  useSetFromValue,
  useSetSrcSchool,
  useSetSrcSchoolyear,
  useSetToValue,
  useSrcSchoolSchoolyear,
  useToValue,
} from './atom';
import { CopyEventsToAnotherDay } from './CopyEventsToAnotherDay';
import { FooterWrapper } from './Footer/Wrapper';
import { Provider } from 'jotai/react';
import { useSchoolSchoolyear } from 'containers/App/context/SchoolyearSchoolProvider';
import { TowColumnsWithArrowInMiddle } from './TwoColumnsWithArrowInMiddle';
import { useClassroomsCopyEvents } from './useClassroomsCopyEvents';
import { useIsBelowBreakpoint } from 'components/ResponsiveSwitcher/useIsBelowSize';

const HydrateAtoms = ({ initialValues, children }) => {
  useHydrateAtoms(initialValues);
  return children;
};

function From(props: { editable: boolean }) {
  const from = useFromValue();
  const to = useToValue();
  const onUpdateFrom = useSetFromValue();
  const { schoolyear, school } = useSrcSchoolSchoolyear();
  const onSelectSchool = useSetSrcSchool();
  const onSelectSchoolyear = useSetSrcSchoolyear();
  const classrooms = useClassroomsCopyEvents({
    schoolId: school?.id,
    schoolyearId: schoolyear?.id,
  });

  if (!schoolyear || !school) {
    return null;
  }

  return (
    <CopyEventsToAnotherDay
      source
      withPreviewEvents
      onChangeDate={onUpdateFrom}
      date={from}
      oppositeDate={to}
      editable={props.editable}
      schoolyear={schoolyear}
      school={school}
      classrooms={classrooms}
      onChangeSchool={onSelectSchool}
      onChangeSchoolyear={onSelectSchoolyear}
    />
  );
}

function To(props: { editable: boolean }) {
  const to = useToValue();
  const from = useFromValue();
  const onUpdateTo = useSetToValue();
  const { schoolyear, school } = useDestSchoolSchoolyear();
  const onSelectSchool = useSetDestSchool();
  const onSelectSchoolyear = useSetDestSchoolyear();
  const classrooms = useClassroomsCopyEvents({
    schoolId: school?.id,
    schoolyearId: schoolyear?.id,
  });

  if (!schoolyear || !school) {
    return null;
  }

  return (
    <CopyEventsToAnotherDay
      onChangeDate={onUpdateTo}
      classrooms={classrooms}
      date={to}
      oppositeDate={from}
      editable={props.editable}
      schoolyear={schoolyear}
      school={school}
      onChangeSchool={onSelectSchool}
      onChangeSchoolyear={onSelectSchoolyear}
    />
  );
}

export function CopyEventsToAnotherDayModal() {
  const payload = useCopyEventsToAnotherDayPayload();
  const closeModal = useCloseCopyEventsToAnotherDayModal();

  const resetSelectedSeances = useResetSelectedPreviewSeances();
  const onClose = () => {
    resetSelectedSeances();
    closeModal();
  };
  const isOpen = !!payload;
  const completeButtonRef = useRef(null);

  const { schoolyear, school } = useSchoolSchoolyear();
  const from = payload?.from;
  const to = payload?.to;
  const isMobile = useIsBelowBreakpoint('md');

  return (
    <Dialog
      size="2xl"
      full={isMobile}
      isOpen={isOpen}
      onClose={onClose}
      disableMaxHeight
      initialFocus={completeButtonRef}
    >
      <Provider>
        <HydrateAtoms
          initialValues={[
            [submitPayloadAtom, { from: from || to, to: to || from }], // init both from and to with the same value
            [
              selectedSchoolSchoolyearAtom,
              {
                srcSchoolyear: schoolyear,
                srcSchool: school,
                destSchoolyear: schoolyear,
                destSchool: school,
              },
            ],
            [selectedPreviewSeancesAtom, payload?.selectedSeances || []],
          ]}
        >
          <TowColumnsWithArrowInMiddle onClose={onClose} isMobile={isMobile}>
            <div data-testid="from">
              <From editable={!from} />
            </div>
            <div data-testid="destination">
              <To editable={!to} />
            </div>
            <FooterWrapper
              ctaTitle={payload?.ctaTitle}
              onSuccess={payload?.onSuccess}
              onClose={onClose}
            />
          </TowColumnsWithArrowInMiddle>
        </HydrateAtoms>
      </Provider>
    </Dialog>
  );
}
